import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { uploadFile, createOffer } from '../services/offerService';
import { createListingManually } from '../services/listingService';
import { ProcessingStep, Listing } from '../types';
import { db } from '../firebase/config';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import './OfferForm.css';

type ListingMode = 'existing' | 'new' | 'in-documents' | null;

const OfferForm: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const [offerFiles, setOfferFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>([]);
  const [error, setError] = useState('');
  const [parsingError, setParsingError] = useState('');
  const [currentOfferId, setCurrentOfferId] = useState<string | null>(null);
  const [completedData, setCompletedData] = useState<any>(null);
  const [offerScore, setOfferScore] = useState<number | null>(null);
  const [animatedScore, setAnimatedScore] = useState(0);

  // Listing selection
  const [userListings, setUserListings] = useState<Listing[]>([]);
  const [listingMode, setListingMode] = useState<ListingMode>(null);
  const [selectedListingId, setSelectedListingId] = useState('');
  const [newListingFields, setNewListingFields] = useState({
    address: '', city: '', state: '', zipCode: '',
    mlsNumber: '', listingPrice: '', listingDate: '',
  });
  const [showNewListingErrors, setShowNewListingErrors] = useState(false);
  const [inDocsConfirmed, setInDocsConfirmed] = useState(false);

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Fetch user's listings
  useEffect(() => {
    if (!currentUser) return;
    const fetchListings = async () => {
      try {
        const listingsQuery = query(
          collection(db, 'listings'),
          where('userId', '==', currentUser.uid)
        );
        const snapshot = await getDocs(listingsQuery);
        const listings: Listing[] = [];
        snapshot.forEach((d) => {
          const data = d.data();
          if (data.address !== 'Pending extraction from purchase agreement') {
            listings.push({ id: d.id, ...data } as Listing);
          }
        });
        // Sort by createdAt descending
        listings.sort((a, b) => {
          const aTime = (a.createdAt as any)?.seconds ?? 0;
          const bTime = (b.createdAt as any)?.seconds ?? 0;
          return bTime - aTime;
        });
        setUserListings(listings);
        if (listingId && listings.some((l) => l.id === listingId)) {
          setSelectedListingId(listingId);
          setListingMode('existing');
        } else if (listings.length > 0) {
          setListingMode('existing');
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
      }
    };
    fetchListings();
  }, [currentUser, listingId]);

  // Real-time offer status listener
  useEffect(() => {
    if (!currentOfferId) return;
    const unsubscribe = onSnapshot(doc(db, 'offers', currentOfferId), (snap) => {
      const data = snap.data();
      if (!data) return;
      if (data.parsingStatus === 'completed') {
        setIsProcessing(false);
        setCompletedData(data);
        if (typeof data.ptcScore === 'number') setOfferScore(data.ptcScore);
        updateStep('extract', { completed: true, inProgress: false });
        updateStep('score', { completed: true, inProgress: false });
      } else if (data.parsingStatus === 'failed' || data.parsingStatus === 'manual_review') {
        setParsingError(data.parsingStatus === 'manual_review' ? 'MANUAL_REVIEW' : (data.parsingError || 'Failed to process offer documents.'));
        setIsProcessing(false);
        setProcessingSteps([]);
      }
    });
    return () => unsubscribe();
  }, [currentOfferId]);

  // Auto-navigate after success
  useEffect(() => {
    if (!completedData || !currentOfferId) return;
    const timer = setTimeout(() => navigate(`/offers/${currentOfferId}`), 2000);
    return () => clearTimeout(timer);
  }, [completedData, currentOfferId, navigate]);

  // Animate score counter
  useEffect(() => {
    if (offerScore === null) return;
    let start = 0;
    const step = Math.ceil(offerScore / 40);
    const timer = setInterval(() => {
      start += step;
      if (start >= offerScore) { setAnimatedScore(offerScore); clearInterval(timer); }
      else setAnimatedScore(start);
    }, 30);
    return () => clearInterval(timer);
  }, [offerScore]);

  const updateStep = (id: string, updates: Partial<ProcessingStep>) => {
    setProcessingSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  // File handlers
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) => f.type === 'application/pdf');
    if (dropped.length) setOfferFiles((prev) => [...prev, ...dropped]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setOfferFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
  };

  const removeFile = (index: number) => setOfferFiles((prev) => prev.filter((_, i) => i !== index));

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (offerFiles.length === 0) { setError('Please upload at least one offer document.'); return; }
    if (!currentUser) { setError('You must be logged in to submit offers.'); return; }

    // Validate new listing fields if mode is 'new'
    if (listingMode === 'new') {
      const f = newListingFields;
      if (!f.address.trim() || !f.city.trim() || !f.state.trim() || !f.mlsNumber.trim() || !f.listingPrice) {
        setShowNewListingErrors(true);
        setError('Please complete the required listing fields highlighted in red.');
        return;
      }
    }

    setIsProcessing(true);
    setError('');
    setParsingError('');
    setProcessingSteps([
      { id: 'upload', label: 'Uploading documents', completed: false, inProgress: true },
      { id: 'extract', label: 'Extracting offer details (AI)', completed: false, inProgress: false },
      { id: 'score', label: 'Calculating PTC score', completed: false, inProgress: false },
    ]);

    try {
      let resolvedListingId: string | undefined;

      // Create listing if adding new
      if (listingMode === 'new') {
        const f = newListingFields;
        resolvedListingId = await createListingManually(currentUser.uid, {
          address: f.address.trim(),
          city: f.city.trim() || undefined,
          state: f.state.trim() || undefined,
          zipCode: f.zipCode.trim() || undefined,
          mlsNumber: f.mlsNumber.trim() || undefined,
          listingPrice: f.listingPrice ? parseFloat(f.listingPrice) : null,
          listingDate: f.listingDate || null,
        });
      } else if (listingMode === 'existing') {
        resolvedListingId = selectedListingId || undefined;
      }
      // 'in-documents' → no listingId, AI extracts from docs

      const uploadedFiles = [];
      for (const file of offerFiles) {
        uploadedFiles.push(await uploadFile(file, currentUser.uid));
      }
      updateStep('upload', { completed: true, inProgress: false });
      updateStep('extract', { inProgress: true });

      const offerId = await createOffer(currentUser.uid, uploadedFiles, resolvedListingId, undefined);
      setCurrentOfferId(offerId);
    } catch (err: any) {
      setError(err.message || 'Failed to submit offer. Please try again.');
      setIsProcessing(false);
      setProcessingSteps([]);
    }
  };

  const handleReset = () => {
    setOfferFiles([]);
    setSelectedListingId(listingId || '');
    setListingMode(null);
    setNewListingFields({ address: '', city: '', state: '', zipCode: '', mlsNumber: '', listingPrice: '', listingDate: '' });
    setShowNewListingErrors(false);
    setInDocsConfirmed(false);
    setIsProcessing(false);
    setProcessingSteps([]);
    setError('');
    setParsingError('');
    setCurrentOfferId(null);
    setCompletedData(null);
    setOfferScore(null);
    setAnimatedScore(0);
  };

  // Score helpers
  const scoreColorClass = offerScore !== null ? (offerScore >= 70 ? 'score-green' : offerScore >= 50 ? 'score-yellow' : 'score-red') : '';
  const scoreMessage = offerScore !== null ? (offerScore >= 70 ? 'Excellent — highly competitive' : offerScore >= 50 ? 'Good — competitive offer' : 'Needs improvement') : '';

  // ── Processing state ──
  if (isProcessing || (currentOfferId && !completedData && !parsingError)) {
    return (
      <div className="offer-form-container">
        <div className="processing-card">
          <div className="processing-title">Processing Offer Documents...</div>
          <p className="processing-subtitle">This may take up to a minute. Please don't close this page.</p>
          <div className="processing-steps">
            {processingSteps.map((step) => (
              <div key={step.id} className={`step-row ${step.completed ? 'completed' : step.inProgress ? 'in-progress' : 'pending'}`}>
                <div className="step-icon">{step.completed ? '✓' : step.inProgress ? '⋯' : '○'}</div>
                <span className="step-label">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Manual review state ──
  if (parsingError === 'MANUAL_REVIEW') {
    return (
      <div className="offer-form-container">
        <div className="review-card">
          <div className="review-icon">📋</div>
          <h3 className="review-title">Offer Received — Under Review</h3>
          <p className="review-body">Your offer documents were received but could not be automatically processed. Our team will review and process your submission manually within 1 business day.</p>
          <p className="review-body">You will receive a confirmation once your offer is visible to the listing agent. No further action is needed.</p>
          <button className="btn-secondary" onClick={handleReset}>Submit Another Offer</button>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (parsingError) {
    return (
      <div className="offer-form-container">
        <div className="error-card">
          <div className="error-icon">✕</div>
          <h3 className="error-title">Processing Failed</h3>
          <p className="error-body">{parsingError}</p>
          <button className="btn-primary" onClick={handleReset}>Try Again</button>
        </div>
      </div>
    );
  }

  // ── Success state ──
  if (completedData) {
    const ext = completedData?.extractedData;
    const propertyAddress = [ext?.property?.address, ext?.property?.city, ext?.property?.state].filter(Boolean).join(', ');
    const buyerName = ext?.parties?.buyers?.[0]?.name;
    const purchasePrice = ext?.transaction?.final_sales_price;
    const loanType = ext?.pre_approval?.loan_type || ext?.financing?.mortgage_type;
    const hasPreApproval = ext?._meta?.has_pre_approval;
    const fmt = (v: number | null | undefined) => v != null ? `$${v.toLocaleString()}` : '—';
    return (
      <div className="offer-form-container">
        <div className="success-card">
          <div className="success-check">✓</div>
          <h2 className="success-title">Offer Processed</h2>
          {offerScore !== null && <div className={`score-circle ${scoreColorClass}`}><span className="score-value">{animatedScore}</span><span className="score-label">PTC Score</span></div>}
          {offerScore !== null && <p className="score-message">{scoreMessage}</p>}
          <div className="summary-table">
            {propertyAddress && <div className="summary-row"><span className="summary-label">Property</span><span className="summary-value">{propertyAddress}</span></div>}
            {buyerName && <div className="summary-row"><span className="summary-label">Buyer</span><span className="summary-value">{buyerName}</span></div>}
            {purchasePrice != null && <div className="summary-row"><span className="summary-label">Purchase Price</span><span className="summary-value">{fmt(purchasePrice)}</span></div>}
            {loanType && <div className="summary-row"><span className="summary-label">Loan Type</span><span className="summary-value">{loanType}</span></div>}
            <div className="summary-row"><span className="summary-label">Pre-Approval</span><span className={`summary-value ${hasPreApproval ? 'text-green' : 'text-red'}`}>{hasPreApproval ? '✓ Yes' : '✗ No'}</span></div>
          </div>
          <div className="success-actions">
            <button className="btn-primary" onClick={() => currentOfferId && navigate(`/offers/${currentOfferId}`)}>View Full Details</button>
            <button className="btn-secondary" onClick={handleReset}>Submit Another Offer</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ──
  const nlf = newListingFields;
  const isNewListingReady = !!(nlf.address.trim() && nlf.city.trim() && nlf.state.trim() && nlf.mlsNumber.trim() && nlf.listingPrice);
  const listingSectionComplete =
    (listingMode === 'existing' && !!selectedListingId) ||
    (listingMode === 'new' && isNewListingReady) ||
    (listingMode === 'in-documents' && inDocsConfirmed);
  const canSubmit = listingSectionComplete && offerFiles.length > 0;

  return (
    <div className="offer-form-container">
      <div className="offer-form-header">
        <button className="back-button" onClick={() => navigate('/listings')}>← Back to Listings</button>
        <h1 className="form-title">Submit Offer</h1>
        <p className="form-subtitle">Upload offer documents for AI-powered scoring</p>
      </div>

      {error && <div className="form-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* ── Section 1: Property Listing ── */}
        <div className="form-section">
          <div className="section-header">
            <h3 className="section-title">Property Listing</h3>
            <p className="section-desc">Tell us which property this offer is for.</p>
          </div>

          <div className="listing-mode-cards">
            {userListings.length > 0 && (
              <button
                type="button"
                className={`listing-mode-card${listingMode === 'existing' ? ' selected' : ''}`}
                onClick={() => setListingMode('existing')}
              >
                <span className="lmc-icon">🏠</span>
                <span className="lmc-title">Select Existing Listing</span>
                <span className="lmc-desc">Choose from your active listings</span>
              </button>
            )}
            <button
              type="button"
              className={`listing-mode-card${listingMode === 'new' ? ' selected' : ''}`}
              onClick={() => setListingMode('new')}
            >
              <span className="lmc-icon">✏️</span>
              <span className="lmc-title">Add Listing Details</span>
              <span className="lmc-desc">Enter the key listing information</span>
            </button>
            <button
              type="button"
              className={`listing-mode-card${listingMode === 'in-documents' ? ' selected' : ''}`}
              onClick={() => setListingMode('in-documents')}
            >
              <span className="lmc-icon">📄</span>
              <span className="lmc-title">Included in Documents</span>
              <span className="lmc-desc">Listing details are in the uploaded files</span>
            </button>
          </div>

          {/* Existing listing dropdown */}
          {listingMode === 'existing' && (
            <div className="listing-mode-panel">
              <label className="input-label">Select Listing</label>
              <select
                className="listing-select"
                value={selectedListingId}
                onChange={(e) => setSelectedListingId(e.target.value)}
              >
                <option value="">— Choose a listing —</option>
                {userListings.map((l) => (
                  <option key={l.id} value={l.id!}>
                    {l.address}{l.city ? `, ${l.city}` : ''}{l.state ? `, ${l.state}` : ''}
                    {l.mlsNumber ? ` (MLS# ${l.mlsNumber})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* New listing inline form */}
          {listingMode === 'new' && (
            <div className="listing-mode-panel">
              <div className="new-listing-grid">
                <div className={`new-listing-field full-width${showNewListingErrors && !nlf.address.trim() ? ' field-err' : ''}`}>
                  <label>Street Address <span className="req-star">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. 123 Main St"
                    value={nlf.address}
                    onChange={e => setNewListingFields(v => ({ ...v, address: e.target.value }))}
                  />
                </div>
                <div className={`new-listing-field${showNewListingErrors && !nlf.city.trim() ? ' field-err' : ''}`}>
                  <label>City <span className="req-star">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Detroit"
                    value={nlf.city}
                    onChange={e => setNewListingFields(v => ({ ...v, city: e.target.value }))}
                  />
                </div>
                <div className={`new-listing-field${showNewListingErrors && !nlf.state.trim() ? ' field-err' : ''}`}>
                  <label>State <span className="req-star">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. MI"
                    value={nlf.state}
                    onChange={e => setNewListingFields(v => ({ ...v, state: e.target.value }))}
                    maxLength={2}
                  />
                </div>
                <div className="new-listing-field">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    placeholder="e.g. 48201"
                    value={nlf.zipCode}
                    onChange={e => setNewListingFields(v => ({ ...v, zipCode: e.target.value }))}
                  />
                </div>
                <div className={`new-listing-field${showNewListingErrors && !nlf.mlsNumber.trim() ? ' field-err' : ''}`}>
                  <label>MLS # <span className="req-star">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. 20240012"
                    value={nlf.mlsNumber}
                    onChange={e => setNewListingFields(v => ({ ...v, mlsNumber: e.target.value }))}
                  />
                </div>
                <div className={`new-listing-field${showNewListingErrors && !nlf.listingPrice ? ' field-err' : ''}`}>
                  <label>Listing Price (LP) <span className="req-star">*</span></label>
                  <input
                    type="number"
                    placeholder="e.g. 450000"
                    value={nlf.listingPrice}
                    onChange={e => setNewListingFields(v => ({ ...v, listingPrice: e.target.value }))}
                    min={0}
                  />
                </div>
                <div className="new-listing-field">
                  <label>Listing Date</label>
                  <input
                    type="date"
                    value={nlf.listingDate}
                    onChange={e => setNewListingFields(v => ({ ...v, listingDate: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* In-documents checkbox confirmation */}
          {listingMode === 'in-documents' && (
            <div className="listing-mode-panel in-docs-note">
              <span className="in-docs-icon">📄</span>
              <div className="in-docs-body">
                <p className="in-docs-text">Our AI will extract the listing details directly from your uploaded documents.</p>
                <label className="in-docs-checkbox-label">
                  <input
                    type="checkbox"
                    checked={inDocsConfirmed}
                    onChange={e => setInDocsConfirmed(e.target.checked)}
                  />
                  I confirm the MLS listing page is included in my uploaded documents
                </label>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 2: Offer Documents (shown only after listing section is complete) ── */}
        {listingSectionComplete && (
          <div className="form-section">
            <div className="section-header">
              <h3 className="section-title">Offer Documents <span className="badge-required">Required</span></h3>
              <p className="section-desc">Upload the purchase agreement and/or pre-approval letter (PDF).</p>
            </div>

            <div
              className={`drop-zone ${isDragging ? 'drop-zone-active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleFileDrop}
              onClick={() => document.getElementById('offer-file-input')?.click()}
            >
              <input id="offer-file-input" type="file" multiple accept=".pdf" style={{ display: 'none' }} onChange={handleFileChange} />
              <div className="drop-zone-icon">📄</div>
              <p className="drop-zone-title">Drag & drop PDF files here</p>
              <p className="drop-zone-sub">or click to browse</p>
            </div>

            {offerFiles.length > 0 && (
              <div className="file-list">
                {offerFiles.map((file, i) => (
                  <div key={i} className="file-item">
                    <span className="file-icon">📄</span>
                    <div className="file-details">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{formatBytes(file.size)}</span>
                    </div>
                    <button type="button" className="remove-btn" onClick={() => removeFile(i)} title="Remove file">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {listingSectionComplete && (
          <button type="submit" className="offer-submit-btn" disabled={!canSubmit}>
            Submit Offer
          </button>
        )}
      </form>
    </div>
  );
};

export default OfferForm;
